export const en = {
  // Navbar
  nav: {
    portfolio: 'Portfolio',
    about: 'About',
    contact: 'Contact',
    bookCall: 'Book a Call',
  },

  // Hero
  hero: {
    badge: 'AI-Powered Web Design',
    title: 'We build websites that',
    titleHighlight: 'wow your customers.',
    subtitle:
      'Quick Designs combines cutting-edge AI technology with creative expertise to craft stunning, eye-catching websites that impress visitors and convert them into customers.',
    cta: 'Book a Call',
    ctaSecondary: 'View Our Work',
  },

  // Portfolio
  portfolio: {
    title: 'Selected Work',
    subtitle:
      'AI-enhanced designs crafted to captivate and convert. Each project combines intelligent design tools with human creativity for maximum impact.',
    cta: 'Start your project',
    viewCase: 'View Case Study',
    projects: {
      fitness: { title: 'Fitness Studio', category: 'Health & Fitness' },
      beauty: { title: 'Beauty Studio', category: 'Beauty & Wellness' },
    },
  },

  // About
  about: {
    title: 'AI-Powered Design Studio',
    description1:
      'Quick Designs harnesses the power of artificial intelligence to create websites that truly stand out. Our AI tools analyze design trends, optimize layouts, and generate stunning visuals that capture attention instantly.',
    description2:
      'We believe great design should be accessible to every business. By combining AI capabilities with human creativity, we deliver premium, eye-catching websites faster and more affordably than traditional agencies—without compromising on quality.',
    description3:
      'Whether you need a bold landing page that stops scrollers in their tracks or a complete brand presence that leaves lasting impressions, our AI-enhanced workflow ensures your website looks exceptional and performs brilliantly.',
    stats: {
      satisfaction: 'Client Satisfaction',
      response: 'Response Time',
      support: 'Support Available',
      revision: 'Unlimited Revisions',
    },
    features: {
      fast: {
        title: 'AI-Enhanced Speed',
        description:
          'Our AI tools accelerate the design process, delivering stunning websites in record time without sacrificing quality or creativity.',
      },
      secure: {
        title: 'Smart & Secure',
        description:
          'AI-optimized code that follows best practices, ensuring your site is fast, secure, and ranks high on search engines.',
      },
      userCentric: {
        title: 'Designs That Impress',
        description:
          'AI-powered layouts and visuals crafted to catch eyes, engage visitors, and turn browsers into loyal customers.',
      },
    },
  },

  // Contact
  contact: {
    title: "Let's Create Something Amazing",
    subtitle:
      'Ready for an AI-powered website that impresses? Book a call to discuss your vision.',
    info: {
      title: 'Contact Information',
    },
    form: {
      title: 'Send a Message',
      name: 'Name',
      namePlaceholder: 'John Doe',
      email: 'Email',
      emailPlaceholder: 'john@example.com',
      message: 'Message',
      messagePlaceholder: 'Tell us about your project...',
      submit: 'Send Message',
      submitting: 'Sending...',
      success: {
        title: 'Message Sent!',
        subtitle: "We'll get back to you within 24 hours.",
        another: 'Send Another Message',
      },
      error: 'Something went wrong. Please try again.',
    },
    booking: {
      title: 'Book a Discovery Call',
    },
  },

  // Footer
  footer: {
    description:
      'AI-powered web design that impresses. We combine artificial intelligence with creative expertise to build stunning websites that captivate your audience.',
    services: {
      title: 'Services',
      items: ['AI Web Design', 'Development', 'E-commerce', 'Maintenance'],
    },
    company: {
      title: 'Company',
      items: ['About Us', 'Portfolio', 'Careers', 'Contact', 'Privacy Policy'],
    },
    copyright: '© 2026 Quick Designs. All rights reserved.',
    madeWith: 'Made with',
    location: 'in Poland',
  },

  // Privacy Policy
  privacy: {
    title: 'Privacy Policy',
    lastUpdated: 'Last Updated',
    sections: {
      intro: {
        title: 'Introduction',
        content:
          'Quick Designs ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or engage our web development services.',
      },
      collect: {
        title: 'Information We Collect',
        content:
          'We may collect information about you in various ways, including:',
        items: [
          'Personal Data: Name, email address, phone number, and business information you provide when contacting us or requesting our services.',
          'Project Information: Details about your business, brand assets, content, and requirements shared during project collaboration.',
          'Usage Data: Information about how you interact with our website, including IP address, browser type, pages visited, and time spent on pages.',
          'Communication Data: Records of correspondence when you contact us via email, contact forms, or scheduling tools.',
        ],
      },
      use: {
        title: 'How We Use Your Information',
        content: 'We use the information we collect to:',
        items: [
          'Provide, maintain, and improve our web development services',
          'Communicate with you about projects, updates, and inquiries',
          'Send you relevant information about our services (with your consent)',
          'Process transactions and send related information',
          'Analyze website usage to improve user experience',
          'Comply with legal obligations',
        ],
      },
      protection: {
        title: 'Data Protection',
        content:
          'We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encrypted connections (SSL/TLS), secure file storage, and limited access to personal data on a need-to-know basis.',
      },
      thirdParty: {
        title: 'Third-Party Services',
        content:
          'We may use third-party services that collect, monitor, and analyze data to improve our services:',
        items: [
          'Cal.com - For scheduling consultations and meetings',
          'Formspree - For processing contact form submissions',
          'Analytics tools - To understand website traffic and usage patterns',
          'Hosting providers - To deliver our website securely and reliably',
        ],
      },
      clientProjects: {
        title: 'Client Project Confidentiality',
        content:
          'All client project information, including business strategies, brand assets, and proprietary content, is treated as strictly confidential. We do not share, sell, or disclose client project details to third parties without explicit written consent. Project files are securely stored and access is limited to team members directly involved in your project.',
      },
      cookies: {
        title: 'Cookies and Tracking',
        content:
          'Our website may use cookies and similar tracking technologies to enhance your browsing experience. You can set your browser to refuse cookies, but some features of our website may not function properly without them. We use essential cookies for site functionality and may use analytics cookies to understand how visitors interact with our website.',
      },
      rights: {
        title: 'Your Rights',
        content:
          'Depending on your location, you may have certain rights regarding your personal information:',
        items: [
          'Access: Request a copy of the personal data we hold about you',
          'Correction: Request correction of inaccurate or incomplete data',
          'Deletion: Request deletion of your personal data',
          'Objection: Object to processing of your personal data',
          'Portability: Request transfer of your data to another service',
          'Withdraw Consent: Withdraw consent for data processing at any time',
        ],
      },
      contact: {
        title: 'Contact Us',
        content:
          'If you have questions about this Privacy Policy or wish to exercise your data rights, please contact us at:',
      },
      changes: {
        title: 'Changes to This Policy',
        content:
          'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this Privacy Policy periodically for any changes.',
      },
    },
  },
}

export type Translations = typeof en
